import "./Quote.css";
import type { OnStateChange } from "@/components/mosaic/ui/TileTitle/TileTitle";
import { NoData } from "@/components/NoData";
import { Spinner } from "@/components/Spinner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuthService } from "@/hooks/useAuthService";
import { cn } from "@/lib/utils";
import { HoverCardWrapper } from "@/shared/components/HoverCardWrapper";
import { OfferToUpgrade } from "@/shared/components/OfferToUpgrade";
import { DEFAULT_STATE_VALUES } from "@/shared/constants/common";
import { mosaicSubToolbarClassNames } from "@/shared/constants/customStyleSets";
import { useExecuteData } from "../lib/hooks";
import { type QuoteState, QuoteType, QuoteTypeLabel } from "../type";
import { AdvancedQuote } from "./components/AdvancedQuote";
import { BasicQuote } from "./components/BasicQuote";

export const Quote = ({
	tileId,
	state,
	onStateChange,
}: {
	tileId: string;
	state: QuoteState;
	onStateChange?: OnStateChange;
}) => {
	const { isAdvancedQuoteAllowed } = useAuthService();

	const ticker = state?.ticker ?? DEFAULT_STATE_VALUES.ticker;
	const rawQuoteType = state?.quoteType ?? QuoteType.BASIC;
	const expandedCharts = state?.expandedCharts;

	const quoteType =
		rawQuoteType === QuoteType.ADVANCED && !isAdvancedQuoteAllowed
			? QuoteType.BASIC
			: rawQuoteType;

	const {
		companyData,
		screenerData,
		financialsData,
		peadData,
		topCompetitorsScreenerData,
		isLoading,
	} = useExecuteData(ticker);

	if (isLoading) return <Spinner />;

	if (
		!ticker ||
		(!companyData &&
			!screenerData &&
			!financialsData?.length &&
			!peadData &&
			!topCompetitorsScreenerData?.length)
	)
		return <NoData />;

	return (
		<Tabs
			value={quoteType}
			onValueChange={(value) => onStateChange?.({ quoteType: value })}
			data-testid="quote"
			className="h-full flex flex-col"
		>
			<TabsList className={cn(mosaicSubToolbarClassNames, "rounded-none")}>
				<HoverCardWrapper
					align="start"
					side="bottom"
					trigger={
						<TabsTrigger
							value={QuoteType.ADVANCED}
							disabled={!isAdvancedQuoteAllowed}
							className="text-sm p-0 h-fit font-bold"
						>
							{QuoteTypeLabel.ADVANCED}
						</TabsTrigger>
					}
					content={
						<OfferToUpgrade
							offer={
								<p>
									To use the Advanced Quote, please upgrade to a Silver plan
								</p>
							}
							buttonText="Upgrade Now"
						/>
					}
					disabled={!isAdvancedQuoteAllowed}
					className="bg-transparent"
				/>

				<TabsTrigger
					value={QuoteType.BASIC}
					className="text-sm p-0 h-fit font-bold"
				>
					{QuoteTypeLabel.BASIC}
				</TabsTrigger>
			</TabsList>

			<div className="flex-1 overflow-y-scroll">
				<TabsContent value={QuoteType.ADVANCED}>
					<AdvancedQuote
						tileId={tileId}
						ticker={ticker}
						screenerData={screenerData}
						financialsData={financialsData}
						peadData={peadData}
						topCompetitorsScreenerData={topCompetitorsScreenerData}
						expandedCharts={expandedCharts}
						onStateChange={onStateChange}
					/>
				</TabsContent>
				<TabsContent value={QuoteType.BASIC}>
					<BasicQuote
						ticker={ticker}
						tileId={tileId}
						companyData={companyData}
						screenerData={screenerData}
						financialsData={financialsData}
						topCompetitorsScreenerData={topCompetitorsScreenerData}
					/>
				</TabsContent>
			</div>
		</Tabs>
	);
};
